import { useMutation } from '@tanstack/react-query'
import useTrackingContext from '../../hooks/useTrackingContext'
import { useOrderApiClient } from '../../OrderApi'
import { useUpsellContext } from '../../UpsellContext'

const SCHUELERAUSTAUSCH_PHONE_PASSING_LIMIT = 6

export const useCreateUpsell = ({
  organisations,
  mutationFn,
  ...mutationOptions
}) => {
  const client = useOrderApiClient()
  const tracker = useTrackingContext()
  const {
    round,
    upsellCount,
    businessArea,
    onSuccess,
    initialOrder
  } = useUpsellContext()

  let upsellOrderTemplate = Object.assign({}, initialOrder, {
    initial_order_id: initialOrder.id,
  })

  // new orders do not have an id!
  delete upsellOrderTemplate['id']

  const createUpsellOrder = async ({ slug, passesPhone }) => {
    let newOrder = Object.assign({}, upsellOrderTemplate, {
      organisation_slug: slug,
      passes_phone: passesPhone,
    })

    return client.createOrder(businessArea, newOrder)
  }

  const createUpsellOrdersFn = async (values) => {
    const selectedItems = values.items

    let currentUpsellCount = upsellCount
    let checkedItems = []

    // we get .entries() here to have access to each item's index
    for await (const [index, item] of selectedItems.entries()) {
      if (item.selected) {
        let passesPhone = true

        if (businessArea === 'schueleraustausch') {
          passesPhone = currentUpsellCount < SCHUELERAUSTAUSCH_PHONE_PASSING_LIMIT
        }

        try {
          // If we do not await this, we cannot disable Submit button
          // during api calls (which might lead to duplicate orders)
          await createUpsellOrder({ slug: item.organisation.slug, passesPhone })

          // Only keep as checked if we successfully created an order
          checkedItems.push(item)
        } catch (err) {
          // Swallow errors to not interrupt the user flow.
          console.log("Creating Upsell Order failed", err)
          // But track exceptions to investigate in analytics
          tracker.trackException(`Creating Upsell Order failed: ${err.message}`)
        }

        currentUpsellCount += 1
      }
    }

    tracker.trackEvent('CatalogOrder', `UpsellForm ${round} submitted`)

    let items = checkedItems.map(({ organisation }) => {
      return({
        id: `${initialOrder.id}-${organisation.shortname}`,
        name: organisation.shortname,
        category: 'CatalogUpsell',
        sku: `CatalogUpsell-${organisation.shortname}`
      })
    })

    tracker.trackEcommerce(`${initialOrder.id}-${round}`, items)

    return {
      seenOrganisations: organisations,
      orderedOrganisations: checkedItems.map(({organisation}) => organisation),
      orderedOrganisationCount: currentUpsellCount,
    }
  }

   return useMutation({
     mutationFn: async(values) => {
       if (mutationFn) {
         values = await mutationFn()
       }

       return await createUpsellOrdersFn(values)
     },
     onSuccess,
     useErrorBoundary: true,
     ...mutationOptions
  })
}
